import { head, isEmpty } from 'ramda';
import { queryClient, queryKeys } from '../../lib/query-client';
import ProjectService from '../../service/project-service';
import ClipService from '../../service/clip-service';
import displayNetworkError from '../../helpers/displayNetworkError';

export const makeMediaActions = (set, get, { refetchCueSheetImpl }) => {
    const fetchVideosImpl = async (id) => {
        set('projectVideosLoading', true);
        set('projectVideosError', null);

        try {
            const data = await queryClient.fetchQuery({
                queryKey: queryKeys.videos(id),
                queryFn: () => ProjectService.getProjectVideosAsync(id),
            });
            const selectedVideo = get('selectedVideo');

            set('projectVideos', data);
            if (!isEmpty(data) && (!selectedVideo || isEmpty(selectedVideo) || selectedVideo === 0)) {
                const firstVideo = head(data);
                set('selectedVideo', firstVideo.id);
            }
            set('projectVideosLoading', false);
        } catch (error) {
            set('projectVideosError', error);
            set('projectVideosLoading', false);
            displayNetworkError(error);

            if (!error?.handled) {
                throw error;
            }
        }
    };

    const refetchVideosImpl = async (id) => {
        await queryClient.invalidateQueries({ queryKey: queryKeys.videos(id) });
        await fetchVideosImpl(id);
    };

    const deleteVideoItemImpl = async (id, videoId) => {
        try {
            const selectedVideo = get('selectedVideo');
            if (selectedVideo === videoId) {
                const projectVideos = get('projectVideos');
                const firstVideo = head(projectVideos);

                if (firstVideo) {
                    set('selectedVideo', firstVideo.id);
                } else {
                    set('selectedVideo', '');
                }
            }

            await ProjectService.deleteProjectVideoItemAsync(id, videoId);
            await refetchVideosImpl(id);
        } catch (error) {
            displayNetworkError(error);

            if (!error?.handled) {
                throw error;
            }
        }
    };

    const deleteClipImpl = async (id) => {
        try {
            await ClipService.deleteClipAsync(id);

            await queryClient.invalidateQueries({ queryKey: queryKeys.cueSheet(id) });
            await refetchCueSheetImpl(id);
        } catch (error) {
            displayNetworkError(error);
            throw error;
        }
    };

    return {
        fetchVideosImpl,
        refetchVideosImpl,

        fetchVideos: fetchVideosImpl,
        refetchVideos: refetchVideosImpl,

        createVideoItem: async (id, formData) => {
            try {
                await ProjectService.createProjectVideoItemAsync(id, formData);

                await queryClient.invalidateQueries({ queryKey: queryKeys.videos(id) });
                await refetchVideosImpl(id);
            } catch (error) {
                displayNetworkError(error);

                if (!error?.handled) {
                    throw error;
                }
            }
        },

        deleteVideoItem: deleteVideoItemImpl,

        // Clips hang off cue sheet items, so a new one shows up through the cue sheet.
        createClipItem: async (id, formData, queryParams) => {
            try {
                await ClipService.createClipAsync(formData, queryParams);
                await refetchCueSheetImpl(id);
            } catch (error) {
                displayNetworkError(error);
                throw error;
            }
        },
        deleteClip: deleteClipImpl,
    };
};
